import type { ReactNode } from "react";
import { css } from "styled-system/css";
import { tbl } from "~/components/editorial/styles";

export interface Band {
  key: string;
  label: string;
}

export interface BandRow {
  key: string;
  label: string;
  /** Preformatted value for this period. */
  value?: ReactNode;
  /** Key of the band the value falls in; omit to show thresholds only. */
  band?: string | null;
  /** Threshold text per band key. */
  thresholds?: Partial<Record<string, string>>;
  /** Outlined label after the metric name, e.g. "Proxy", with a hover title. */
  flag?: { label: string; title?: string };
  /** Short explanation shown under the label. */
  note?: string;
}

/**
 * Performance bands as a ranked, monochrome scale: best to worst left to
 * right, thresholds in each cell, and the band this period falls in filled
 * with ink. No band colors.
 */
export function BandScale({ bands, rows }: { bands: Band[]; rows: BandRow[] }) {
  return (
    <div className={tbl.scroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Metric</th>
            <th data-num>This period</th>
            {bands.map((b, i) => (
              <th key={b.key} className={styles.bandTh}>
                <span className={styles.rank}>{i + 1}</span> {b.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td className={styles.metric}>
                <span className={styles.metricName}>{r.label}</span>
                {r.flag && (
                  <span className={tbl.flag} title={r.flag.title}>
                    {r.flag.label}
                  </span>
                )}
                {r.note && <div className={styles.note}>{r.note}</div>}
              </td>
              <td data-num className={styles.value}>
                {r.value ?? "–"}
              </td>
              {bands.map((b) => {
                const here = r.band === b.key;
                return (
                  <td
                    key={b.key}
                    className={styles.cell}
                    data-here={here || undefined}
                    aria-label={here ? `${b.label}: this period` : undefined}
                  >
                    {here && <span className={styles.marker}>▼ Here</span>}
                    <span className={styles.threshold}>
                      {r.thresholds?.[b.key] ?? ""}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const styles = {
  table: css({
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    minWidth: "760px",
    "& th": {
      fontFamily: "sans",
      fontSize: "10px",
      fontWeight: 700,
      letterSpacing: "0.07em",
      textTransform: "uppercase",
      color: "ed.ink3",
      textAlign: "left",
      py: "6px",
      px: "8px",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.ink",
    },
    "& th:first-child": { width: "26%" },
    "& th:nth-child(2)": { width: "14%" },
    "& td": {
      py: "8px",
      px: "8px",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.rule",
      verticalAlign: "top",
    },
    "& [data-num]": { textAlign: "right" },
  }),
  bandTh: css({ borderLeft: "1px solid var(--colors-ed-rule)" }),
  rank: css({ fontFamily: "mono", color: "ed.ink3", fontWeight: 400 }),
  metric: css({ fontFamily: "sans", fontSize: "13px" }),
  metricName: css({ fontWeight: 700, color: "ed.ink" }),
  note: css({
    fontSize: "11.5px",
    color: "ed.ink3",
    mt: "2px",
    lineHeight: 1.4,
  }),
  value: css({
    fontFamily: "mono",
    fontVariantNumeric: "tabular-nums",
    fontSize: "15px",
    fontWeight: 600,
    color: "ed.ink",
    whiteSpace: "nowrap",
  }),
  cell: css({
    borderLeft: "1px solid var(--colors-ed-rule)",
    fontFamily: "sans",
    fontSize: "11.5px",
    color: "ed.ink3",
    lineHeight: 1.35,
    "&[data-here]": { bg: "ed.ink", color: "ed.paper" },
  }),
  marker: css({
    display: "block",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    mb: "2px",
  }),
  threshold: css({ display: "block" }),
};
